import debounce from 'lodash.debounce'
import { useEffect, useMemo, useRef } from 'react'

import {
  type AsyncSelectState,
  type FetchItemByIdFunction,
  type FetchItemsByTermFunction,
  type FetchItemsFunction,
  type OptionEntry,
  type OptionEntryValue,
} from './model'

import { useAtom } from 'jotai'
import { useAtomCallback } from 'jotai/utils'
import {
  onValueChangeCallback,
  resetCallback,
  resetSearchCallback,
  searchItemsCallback,
} from './callbacks'
import { asyncSelectState } from './state'

export const memo = <R, T extends (arg: any) => R>(fn: T): T => {
  const memoMap = new Map()

  const memoized = (fArg: any): R =>
    memoMap.has(fArg)
      ? memoMap.get(fArg)
      : memoMap.set(fArg, fn(fArg)).get(fArg)

  memoized.$$clear = () => {
    memoMap.clear()
  }

  return memoized as unknown as T
}

interface UseAsyncSelectOut {
  status: AsyncSelectState['status']
  loading: AsyncSelectState['loading']
  searching: AsyncSelectState['loading']
  options: OptionEntry[]
  handleSearch: (s: string) => string
  hint: string | undefined
  hintNoResults: string | undefined
  updateOptions: (v: OptionEntryValue | OptionEntryValue[]) => void
}

const useAsyncSelect = ({
  key,
  value,
  maxItems,
  fetchItems,
  fetchItemsByTerm,
  fetchItemById,
  labels,
}: {
  key: string
  value: OptionEntryValue | OptionEntryValue[] | undefined
  maxItems: number
  fetchItems: FetchItemsFunction
  fetchItemsByTerm: FetchItemsByTermFunction
  fetchItemById: FetchItemByIdFunction
  labels: {
    loading: string
    moreResults: string
    noResults: string
  }
}): UseAsyncSelectOut => {
  const [
    {
      status,
      loading,
      searching,
      initialOptions,
      initialOptionsCount,
      searchOptions,
      searchOptionsCount,
    },
  ] = useAtom(asyncSelectState(key))

  const memoFetchItems = memo(fetchItems)
  const memoFetchItemsByTerm = memo(fetchItemsByTerm)
  const memoFetchItemById = memo(fetchItemById)

  const onValueChange = useAtomCallback(onValueChangeCallback)
  const searchItems = useAtomCallback(searchItemsCallback)
  const resetSearch = useAtomCallback(resetSearchCallback)
  const reset = useAtomCallback(resetCallback)

  const updateOptions = (
    value: OptionEntryValue | OptionEntryValue[],
  ): void => {
    void onValueChange(key, value, memoFetchItems, memoFetchItemById)
  }

  useEffect(() => {
    void onValueChange(key, value, memoFetchItems, memoFetchItemById)
  }, [value, key])

  useEffect(() => {
    return () => {
      reset(key)
    }
  }, [reset, key])

  const options = useMemo(() => {
    return searching
      ? [
          ...searchOptions.filter(
            ({ value }) =>
              initialOptions.find(({ value: v }) => v === value) === undefined,
          ),
          ...initialOptions,
        ]
      : initialOptions
  }, [searching, initialOptions, searchOptions])

  //

  const hint = useMemo(() => {
    return loading
      ? labels.loading
      : searching && searchOptionsCount > maxItems
        ? labels.moreResults
        : !searching && initialOptionsCount > maxItems
          ? labels.moreResults
          : undefined
  }, [
    loading,
    searching,
    initialOptionsCount,
    searchOptionsCount,
    labels.loading,
    labels.moreResults,
    maxItems,
  ])

  const hintNoResults = useMemo(() => {
    return !loading && searching && searchOptionsCount === 0
      ? labels.noResults
      : undefined
  }, [loading, searching, searchOptionsCount, labels.noResults])

  const onSearch = (searchTerm: string | undefined): string | undefined => {
    if (searchTerm !== undefined && searchTerm.length > 0) {
      void searchItems(key, searchTerm, memoFetchItemsByTerm)
    } else {
      resetSearch(key)
    }

    return searchTerm
  }

  const { current: debouncedOnSearch } = useRef(debounce(onSearch, 100))

  const handleSearch = (s: string): string => {
    debouncedOnSearch.cancel()
    debouncedOnSearch(s)
    return s
  }

  return {
    status,
    loading,
    searching,
    options,
    handleSearch,
    hint,
    hintNoResults,
    updateOptions,
  }
}

export default useAsyncSelect
